/**
 * O editor de um modelo de e-mail (mig 791; D36): assunto, abertura, cópia fixa, campos por clique, prévia e teste.
 * 🔴 Só essas três partes se editam: o resumo do técnico, a tabela da visita e o botão do relatório são fixos (é ali que
 * mora "a pendência nunca vai ao cliente", D23). O erro aparece no CAMPO, com foco (formulário nunca é surdo).
 */
import { useRef, useState } from 'react';

import { Button, Input, Label } from '@/components/ui';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/components/ui/Toast';
import { api, ApiError } from '@/lib/api';

import { mensagemDoErro } from '@/pages/os-sistema/os-api';

export interface TextoDoModelo {
  assunto: string;
  abertura: string;
  copia: string[];
  personalizado: boolean;
  textoMudadoPor: string | null;
  textoMudadoEm: string | null;
}

export interface ModeloEditavel {
  chave: string;
  nome: string;
  /** 'fixo' = os e-mails digitados aqui são o PARA (a Route, que não está no cadastro); 'cliente' = a cópia fixa. */
  destino?: 'cliente' | 'fixo';
  /** false = sem campo de cópia fixa (os e-mails do Portal levam o link de entrar, que só serve a quem o recebe). */
  temCopia?: boolean;
  /** O nome e a dica do campo de texto (o atendimento chama de "Abertura"; a confirmação do orçamento, de "Texto"). */
  rotuloDoTexto?: string;
  dicaDoTexto?: string;
  texto: TextoDoModelo;
  padrao: { assunto: string; abertura: string };
  variaveis: Array<{ nome: string; descricao: string }>;
}

type Campo = 'assunto' | 'abertura' | 'copia' | 'para';
const B = '/api/v1/admin/email/modelos';
const classeDoTexto =
  'w-full rounded-xl border border-line bg-paper-surface px-4 py-2 text-sm text-ink-1 focus:border-red focus:outline-none focus:ring-2 focus:ring-red-soft dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1';

export function EditorDoModeloDeEmail<M extends ModeloEditavel>({ modelo, aoSalvar }: { modelo: M; aoSalvar: (modelos: M[]) => void }) {
  const [assunto, setAssunto] = useState(modelo.texto.assunto);
  const [abertura, setAbertura] = useState(modelo.texto.abertura);
  const [copia, setCopia] = useState(modelo.texto.copia.join(', '));
  const [para, setPara] = useState('');
  const [erro, setErro] = useState<{ campo: Campo | null; mensagem: string } | null>(null);
  const [previa, setPrevia] = useState<{ assunto: string; html: string } | null>(null);
  const [ocupado, setOcupado] = useState<'salvar' | 'previa' | 'teste' | 'padrao' | null>(null);
  const ultimo = useRef<'assunto' | 'abertura'>('abertura');
  const refs = {
    assunto: useRef<HTMLInputElement>(null),
    abertura: useRef<HTMLTextAreaElement>(null),
    copia: useRef<HTMLInputElement>(null),
    para: useRef<HTMLInputElement>(null),
  };
  const confirmar = useConfirmDialog();
  const { show } = useToast();
  const corpo = () => ({ assunto, abertura, copia });

  function falhou(e: unknown) {
    const campo = e instanceof ApiError ? ((e.response as { campo?: Campo } | undefined)?.campo ?? null) : null;
    setErro({ campo, mensagem: mensagemDoErro(e) });
    if (campo) refs[campo].current?.focus();
  }

  function inserir(nome: string) {
    const alvo = ultimo.current;
    const el = refs[alvo].current;
    const atual = alvo === 'assunto' ? assunto : abertura;
    const ini = el?.selectionStart ?? atual.length;
    const fim = el?.selectionEnd ?? atual.length;
    const novo = `${atual.slice(0, ini)}{{${nome}}}${atual.slice(fim)}`;
    (alvo === 'assunto' ? setAssunto : setAbertura)(novo);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = ini + nome.length + 4;
      el?.setSelectionRange(pos, pos);
    });
  }

  async function rodar(qual: 'salvar' | 'previa' | 'teste', fazer: () => Promise<void>) {
    setErro(null);
    setOcupado(qual);
    try {
      await fazer();
    } catch (e) {
      falhou(e);
    } finally {
      setOcupado(null);
    }
  }

  const salvar = () =>
    rodar('salvar', async () => {
      const r = await api.put<{ modelos: M[] }>(`${B}/${encodeURIComponent(modelo.chave)}/texto`, corpo());
      aoSalvar(r.modelos);
      show('Texto salvo.', { variant: 'success' });
    });

  const verPrevia = () =>
    rodar('previa', async () => {
      setPrevia(await api.post<{ assunto: string; html: string }>(`${B}/${encodeURIComponent(modelo.chave)}/previa`, corpo()));
    });

  const testar = () =>
    rodar('teste', async () => {
      if (!para.trim()) {
        setErro({ campo: 'para', mensagem: 'Digite o e-mail que recebe o teste.' });
        refs.para.current?.focus();
        return;
      }
      await api.post(`${B}/${encodeURIComponent(modelo.chave)}/teste`, { ...corpo(), para: para.trim() });
      show(`Teste enviado para ${para.trim()}.`, { variant: 'success' });
    });

  async function voltarAoPadrao() {
    const ok = await confirmar({
      title: 'Voltar ao texto padrão?',
      message: 'O assunto, a abertura e a cópia fixa voltam ao texto de quando o e-mail nasceu.',
      confirmLabel: 'Voltar ao padrão',
    });
    if (!ok) return;
    setErro(null);
    setOcupado('padrao');
    try {
      const r = await api.delete<{ modelos: M[] }>(`${B}/${encodeURIComponent(modelo.chave)}/texto`);
      aoSalvar(r.modelos);
      setAssunto(modelo.padrao.assunto);
      setAbertura(modelo.padrao.abertura);
      setCopia('');
      setPrevia(null);
      show('Texto padrão de volta.', { variant: 'success' });
    } catch (e) {
      falhou(e);
    } finally {
      setOcupado(null);
    }
  }

  const erroDe = (c: Campo) =>
    erro?.campo === c ? (
      <p id={`email-erro-${c}`} role="alert" className="mt-1 text-sm font-semibold text-red-deep dark:text-red-glow">
        {erro.mensagem}
      </p>
    ) : null;

  return (
    <div className="mt-3 flex flex-col gap-3 border-t border-line-soft pt-3 dark:border-line-soft-dark" data-testid={`editor-email-${modelo.chave}`}>
      <div>
        <Label htmlFor={`email-assunto-${modelo.chave}`}>Assunto</Label>
        <Input
          id={`email-assunto-${modelo.chave}`}
          ref={refs.assunto}
          value={assunto}
          onChange={(e) => setAssunto(e.target.value)}
          onFocus={() => (ultimo.current = 'assunto')}
          aria-invalid={erro?.campo === 'assunto'}
          aria-describedby={erro?.campo === 'assunto' ? 'email-erro-assunto' : undefined}
        />
        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">Sai sempre em letras maiúsculas.</p>
        {erroDe('assunto')}
      </div>
      <div>
        <Label htmlFor={`email-abertura-${modelo.chave}`}>{modelo.rotuloDoTexto ?? 'Abertura'}</Label>
        <textarea
          id={`email-abertura-${modelo.chave}`}
          ref={refs.abertura}
          rows={modelo.rotuloDoTexto === 'Texto' ? 9 : 3}
          value={abertura}
          onChange={(e) => setAbertura(e.target.value)}
          onFocus={() => (ultimo.current = 'abertura')}
          aria-invalid={erro?.campo === 'abertura'}
          aria-describedby={erro?.campo === 'abertura' ? 'email-erro-abertura' : undefined}
          className={classeDoTexto}
        />
        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">{modelo.dicaDoTexto ?? 'Vem antes do resumo do técnico, que não se edita.'}</p>
        {erroDe('abertura')}
      </div>
      <div>
        <p className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">Campos que se preenchem sozinhos</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {modelo.variaveis.map((v) => (
            <button
              key={v.nome}
              type="button"
              title={v.descricao}
              onClick={() => inserir(v.nome)}
              className="min-h-11 rounded-lg border border-line px-3 font-mono text-xs text-ink-1 hover:bg-paper md:min-h-8 dark:border-line-dark dark:text-ink-dark-1 dark:hover:bg-paper-dark"
            >
              {`{{${v.nome}}}`}
            </button>
          ))}
        </div>
      </div>
      {modelo.temCopia === false ? null : (
      <div>
        <Label htmlFor={`email-copia-${modelo.chave}`}>{modelo.destino === 'fixo' ? 'Para (e-mails da Route)' : 'Cópia fixa'}</Label>
        <Input
          id={`email-copia-${modelo.chave}`}
          ref={refs.copia}
          value={copia}
          onChange={(e) => setCopia(e.target.value)}
          placeholder="nome@igmsecurity.com.br"
          aria-invalid={erro?.campo === 'copia'}
          aria-describedby={erro?.campo === 'copia' ? 'email-erro-copia' : undefined}
        />
        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
          {modelo.destino === 'fixo'
            ? 'Quem recebe este e-mail, separado por vírgula. Sem e-mail aqui, nada sai. A Manutenção recebe cópia.'
            : 'E-mails da IGM que recebem cópia de todo envio, separados por vírgula. A Manutenção já recebe.'}
        </p>
        {erroDe('copia')}
      </div>
      )}
      {erro && !erro.campo ? (
        <p role="alert" className="text-sm font-semibold text-red-deep dark:text-red-glow">
          {erro.mensagem}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void salvar()} disabled={ocupado !== null} className="min-h-[44px] md:min-h-0">
          {ocupado === 'salvar' ? 'Salvando…' : 'Salvar texto'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => void verPrevia()} disabled={ocupado !== null} className="min-h-[44px] md:min-h-0">
          {ocupado === 'previa' ? 'Montando…' : 'Prévia'}
        </Button>
        {modelo.texto.personalizado ? (
          <Button size="sm" variant="ghost" onClick={() => void voltarAoPadrao()} disabled={ocupado !== null} className="min-h-[44px] md:min-h-0">
            Voltar ao texto padrão
          </Button>
        ) : null}
      </div>
      <div>
        <Label htmlFor={`email-teste-${modelo.chave}`}>Enviar teste para</Label>
        <div className="flex flex-wrap gap-2">
          <Input
            id={`email-teste-${modelo.chave}`}
            ref={refs.para}
            type="email"
            value={para}
            onChange={(e) => setPara(e.target.value)}
            placeholder="seu@igmsecurity.com.br"
            className="min-w-0 flex-1"
            aria-invalid={erro?.campo === 'para'}
            aria-describedby={erro?.campo === 'para' ? 'email-erro-para' : undefined}
          />
          <Button size="sm" variant="secondary" onClick={() => void testar()} disabled={ocupado !== null} className="min-h-[44px] md:min-h-0">
            {ocupado === 'teste' ? 'Enviando…' : 'Enviar teste'}
          </Button>
        </div>
        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
          Manda {modelo.chave.startsWith('orcamento_') ? 'um orçamento' : modelo.chave.startsWith('portal_') ? 'um e-mail' : 'um atendimento'} de exemplo, com [TESTE] no
          assunto. Não vai a cliente nenhum{modelo.chave.startsWith('portal_') ? ', e o link do exemplo não entra no Portal' : ''}.
        </p>
        {erroDe('para')}
      </div>
      {previa ? (
        <div data-testid="previa-do-email" className="overflow-hidden rounded-lg border border-line dark:border-line-dark">
          <p className="border-b border-line-soft bg-paper px-3 py-2 text-xs text-ink-2 dark:border-line-soft-dark dark:bg-paper-dark dark:text-ink-dark-2">
            Assunto: <span className="font-semibold">{previa.assunto}</span>
          </p>
          <iframe title="Prévia do e-mail" srcDoc={previa.html} sandbox="" className="h-[520px] w-full bg-white" />
        </div>
      ) : null}
    </div>
  );
}
